import { computed, defineComponent, h, ref } from 'vue';
import {
  Button,
  Card,
  Checkbox,
  CheckboxGroup,
  Col,
  DatePicker,
  DateRangePicker,
  Dialog,
  DialogPlugin,
  Empty,
  Form,
  FormItem,
  Input,
  InputNumber,
  MessagePlugin,
  Option,
  Pagination,
  Progress,
  Radio,
  RadioGroup,
  Row,
  Select,
  Slider,
  Switch,
  Table,
  Tag,
  Textarea,
} from 'tdesign-vue-next';

const themeMap = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'default',
  default: 'default',
};

const variantMap = {
  text: 'text',
  plain: 'outline',
  default: 'base',
};

const ElMessage = {
  success: (content) => MessagePlugin.success(content),
  error: (content) => MessagePlugin.error(content),
  info: (content) => MessagePlugin.info(content),
  warning: (content) => MessagePlugin.warning(content),
};

const ElMessageBox = {
  confirm(content, title = '提示', options = {}) {
    return new Promise((resolve, reject) => {
      const dialog = DialogPlugin.confirm({
        header: title,
        body: content,
        confirmBtn: options.confirmButtonText || '确定',
        cancelBtn: options.cancelButtonText || '取消',
        theme: options.type === 'warning' ? 'warning' : 'default',
        onConfirm: () => {
          dialog.destroy();
          resolve('confirm');
        },
        onClose: () => {
          dialog.destroy();
          reject('cancel');
        },
      });
    });
  },
};

const passthrough = (name, tag = 'div') =>
  defineComponent({
    name,
    inheritAttrs: false,
    setup(props, { attrs, slots }) {
      const className = name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      return () => h(tag, { ...attrs, class: [className, attrs.class] }, slots.default?.());
    },
  });

const ElButton = defineComponent({
  name: 'ElButton',
  inheritAttrs: false,
  props: {
    type: String,
    size: String,
    loading: Boolean,
    disabled: Boolean,
    nativeType: String,
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Button,
        {
          ...attrs,
          theme: themeMap[props.type] || 'default',
          variant: variantMap[props.type] || (props.type ? 'base' : 'outline'),
          size: props.size === 'large' ? 'large' : props.size === 'small' ? 'small' : 'medium',
          loading: props.loading,
          disabled: props.disabled,
          type: props.nativeType || attrs.type,
        },
        slots
      );
  },
});

const ElCard = defineComponent({
  name: 'ElCard',
  inheritAttrs: false,
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Card,
        {
          ...attrs,
          bordered: attrs.bordered !== false,
        },
        slots
      );
  },
});

const ElInput = defineComponent({
  name: 'ElInput',
  inheritAttrs: false,
  props: {
    modelValue: [String, Number],
    type: String,
  },
  emits: ['update:modelValue', 'input', 'change'],
  setup(props, { attrs, emit, slots }) {
    const { prefixIcon, 'prefix-icon': legacyPrefixIcon, ...inputAttrs } = attrs;
    void prefixIcon;
    void legacyPrefixIcon;
    const syncValue = (value) => {
      emit('update:modelValue', value);
      emit('input', value);
    };
    return () => {
      const component = props.type === 'textarea' ? Textarea : Input;
      const componentProps = {
        ...inputAttrs,
        modelValue: props.modelValue,
        'onUpdate:modelValue': syncValue,
        onChange: (value) => {
          syncValue(value);
          emit('change', value);
        },
      };
      if (props.type && props.type !== 'textarea') {
        componentProps.type = props.type;
      }
      return h(component, componentProps, slots);
    };
  },
});

const ElSelect = defineComponent({
  name: 'ElSelect',
  inheritAttrs: false,
  props: {
    modelValue: [String, Number, Array],
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit, slots }) {
    return () =>
      h(
        Select,
        {
          ...attrs,
          value: props.modelValue,
          clearable: attrs.clearable,
          filterable: attrs.filterable,
          onChange: (value) => {
            emit('update:modelValue', value);
            emit('change', value);
          },
        },
        slots
      );
  },
});

const ElRadioGroup = defineComponent({
  name: 'ElRadioGroup',
  inheritAttrs: false,
  props: {
    modelValue: [String, Number, Boolean],
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit, slots }) {
    return () =>
      h(
        RadioGroup,
        {
          ...attrs,
          value: props.modelValue,
          onChange: (value) => {
            emit('update:modelValue', value);
            emit('change', value);
          },
        },
        slots
      );
  },
});

const ElRadio = defineComponent({
  name: 'ElRadio',
  inheritAttrs: false,
  props: {
    label: {
      type: [String, Number, Boolean],
      default: undefined,
    },
    value: {
      type: [String, Number, Boolean],
      default: undefined,
    },
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Radio,
        {
          ...attrs,
          value: props.value ?? props.label,
        },
        slots
      );
  },
});

const ElOption = defineComponent({
  name: 'ElOption',
  inheritAttrs: false,
  props: {
    label: [String, Number],
    value: [String, Number],
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Option,
        {
          ...attrs,
          label: props.label,
          value: props.value,
        },
        slots
      );
  },
});

const ElForm = defineComponent({
  name: 'ElForm',
  inheritAttrs: false,
  props: {
    model: Object,
    data: Object,
    rules: Object,
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Form,
        {
          ...attrs,
          data: props.model || props.data,
          rules: props.rules,
        },
        slots
      );
  },
});

const ElTableColumn = defineComponent({
  name: 'ElTableColumn',
  props: {
    prop: String,
    label: String,
    width: [String, Number],
    minWidth: [String, Number],
    sortable: [Boolean, String],
  },
  setup() {
    return () => null;
  },
});

const getColumnNodes = (nodes = []) =>
  nodes.flatMap((node) => {
    if (!node) return [];
    if (Array.isArray(node.children)) return getColumnNodes(node.children);
    return node.type?.name === 'ElTableColumn' ? [node] : [];
  });

const isTimeColumn = (props = {}) => {
  const prop = String(props.prop || '').toLowerCase();
  const label = String(props.label || props.title || '');
  return (
    props.sortable ||
    label.includes('时间') ||
    label.includes('日期') ||
    /(^|_)(time|date)$/.test(prop) ||
    /(time|date|at)$/.test(prop)
  );
};

const normalizeSort = (sort) => {
  if (!sort) return null;
  if (Array.isArray(sort)) return normalizeSort(sort[0]);
  if (sort.sortBy) return sort;
  if (sort.prop) {
    return {
      sortBy: sort.prop,
      descending: sort.order === 'descending' || sort.order === 'desc',
    };
  }
  return null;
};

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined || value === '') return Number.NEGATIVE_INFINITY;
  const time = new Date(value).getTime();
  if (!Number.isNaN(time)) return time;
  const numeric = Number(value);
  if (!Number.isNaN(numeric)) return numeric;
  return String(value);
};

const compareValues = (left, right) => {
  const a = normalizeComparableValue(left);
  const b = normalizeComparableValue(right);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'zh-Hans-CN', { numeric: true });
};

const buildColumns = (slots) =>
  getColumnNodes(slots.default?.()).map((node, index) => {
    const props = node.props || {};
    const title = props.label || props.title || '';
    const colKey = props.prop || `operation_${index}`;
    const scopedSlot = node.children?.default;
    const sortable = isTimeColumn(props);
    return {
      colKey,
      title,
      width: props.width,
      minWidth: props.minWidth || props['min-width'],
      sortType: sortable ? 'all' : undefined,
      sorter: sortable ? (a, b) => compareValues(a[colKey], b[colKey]) : undefined,
      cell: scopedSlot
        ? (hRenderer, context) => scopedSlot({ row: context.row, $index: context.rowIndex })
        : undefined,
    };
  });

const ElTable = defineComponent({
  name: 'ElTable',
  inheritAttrs: false,
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    border: Boolean,
  },
  emits: ['sort-change'],
  setup(props, { attrs, emit, slots, expose }) {
    const initialSort = normalizeSort(attrs.defaultSort || attrs['default-sort']);
    const sortState = ref(initialSort);
    expose({
      doLayout: () => {},
      clearSort: () => {
        sortState.value = null;
      },
    });
    const sortedData = computed(() => {
      const sort = normalizeSort(sortState.value);
      if (!sort?.sortBy) return props.data;
      return [...props.data].sort((a, b) => {
        const result = compareValues(a[sort.sortBy], b[sort.sortBy]);
        return sort.descending ? -result : result;
      });
    });

    const handleSortChange = (sort, options) => {
      const nextSort = normalizeSort(sort);
      sortState.value = nextSort;
      emit('sort-change', {
        prop: nextSort?.sortBy || '',
        order: nextSort ? (nextSort.descending ? 'descending' : 'ascending') : '',
        column: options?.col,
      });
    };

    return () =>
      h(Table, {
        ...attrs,
        class: ['admin-compat-table', attrs.class],
        data: sortedData.value,
        columns: buildColumns(slots),
        bordered: props.border || attrs.border,
        rowKey: attrs.rowKey || attrs['row-key'] || 'id',
        stripe: attrs.stripe,
        sort: sortState.value,
        onSortChange: handleSortChange,
      });
  },
});

const ElPagination = defineComponent({
  name: 'ElPagination',
  inheritAttrs: false,
  props: {
    total: Number,
    pageSize: Number,
    currentPage: Number,
  },
  emits: ['update:currentPage', 'current-change'],
  setup(props, { attrs, emit }) {
    return () =>
      h(Pagination, {
        ...attrs,
        class: ['admin-compat-pagination', attrs.class],
        total: props.total || 0,
        pageSize: props.pageSize || attrs.pageSize || attrs['page-size'] || 10,
        current: props.currentPage || attrs.current || 1,
        onCurrentChange: (current) => {
          emit('update:currentPage', current);
          emit('current-change', current);
        },
      });
  },
});

const ElDialog = defineComponent({
  name: 'ElDialog',
  inheritAttrs: false,
  props: {
    modelValue: Boolean,
    title: String,
    width: [String, Number],
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { attrs, emit, slots }) {
    return () =>
      h(
        Dialog,
        {
          ...attrs,
          visible: props.modelValue,
          header: props.title,
          width: props.width,
          onClose: () => {
            emit('update:modelValue', false);
            emit('close');
          },
          onConfirm: () => emit('update:modelValue', false),
        },
        slots
      );
  },
});

const ElTag = defineComponent({
  name: 'ElTag',
  inheritAttrs: false,
  props: {
    type: String,
  },
  setup(props, { attrs, slots }) {
    return () => h(Tag, { ...attrs, theme: themeMap[props.type] || 'default' }, slots);
  },
});

const ElSwitch = defineComponent({
  name: 'ElSwitch',
  inheritAttrs: false,
  props: {
    modelValue: Boolean,
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit }) {
    return () =>
      h(Switch, {
        ...attrs,
        value: props.modelValue,
        onChange: (value) => {
          emit('update:modelValue', value);
          emit('change', value);
        },
      });
  },
});

const ElSlider = defineComponent({
  name: 'ElSlider',
  inheritAttrs: false,
  props: {
    modelValue: Number,
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit }) {
    return () =>
      h(Slider, {
        ...attrs,
        value: props.modelValue,
        onChange: (value) => {
          emit('update:modelValue', value);
          emit('change', value);
        },
      });
  },
});

const ElProgress = defineComponent({
  name: 'ElProgress',
  inheritAttrs: false,
  props: {
    percentage: [Number, String],
    type: String,
    strokeWidth: [Number, String],
    color: String,
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        Progress,
        {
          ...attrs,
          percentage: Number(props.percentage || 0),
          status: 'active',
          theme: props.type === 'circle' ? 'circle' : 'line',
          strokeWidth: Number(props.strokeWidth || 6),
          color: props.color,
        },
        slots
      );
  },
});

const ElDatePicker = defineComponent({
  name: 'ElDatePicker',
  inheritAttrs: false,
  props: {
    modelValue: [String, Array, Date],
    type: String,
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit }) {
    const Picker = props.type?.includes('range') ? DateRangePicker : DatePicker;
    return () =>
      h(Picker, {
        ...attrs,
        value: props.modelValue,
        onChange: (value) => {
          emit('update:modelValue', value);
          emit('change', value);
        },
      });
  },
});

const ElInputNumber = defineComponent({
  name: 'ElInputNumber',
  inheritAttrs: false,
  props: {
    modelValue: [Number, String],
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit }) {
    const syncValue = (value) => emit('update:modelValue', value);
    return () =>
      h(InputNumber, {
        ...attrs,
        modelValue: props.modelValue,
        'onUpdate:modelValue': syncValue,
        onChange: (value) => {
          syncValue(value);
          emit('change', value);
        },
      });
  },
});

const ElEmpty = defineComponent({
  name: 'ElEmpty',
  inheritAttrs: false,
  props: {
    description: String,
  },
  setup(props, { attrs, slots }) {
    return () => h(Empty, { ...attrs, description: props.description }, slots);
  },
});

const ElCheckboxGroup = defineComponent({
  name: 'ElCheckboxGroup',
  inheritAttrs: false,
  props: {
    modelValue: Array,
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { attrs, emit, slots }) {
    return () =>
      h(
        CheckboxGroup,
        {
          ...attrs,
          value: props.modelValue,
          onChange: (value) => {
            emit('update:modelValue', value);
            emit('change', value);
          },
        },
        slots
      );
  },
});

const ElCheckbox = defineComponent({
  name: 'ElCheckbox',
  inheritAttrs: false,
  props: {
    label: [String, Number],
  },
  setup(props, { attrs, slots }) {
    return () => h(Checkbox, { ...attrs, value: props.label }, slots);
  },
});

const adapterComponents = {
  ElButton,
  ElCard,
  ElCheckbox,
  ElCheckboxGroup,
  ElCol: Col,
  ElContainer: passthrough('ElContainer', 'section'),
  ElDatePicker,
  ElDialog,
  ElEmpty,
  ElFooter: passthrough('ElFooter', 'footer'),
  ElForm,
  ElFormItem: FormItem,
  ElHeader: passthrough('ElHeader', 'header'),
  ElIcon: passthrough('ElIcon', 'span'),
  ElInput,
  ElInputNumber,
  ElMain: passthrough('ElMain', 'main'),
  ElOption,
  ElPagination,
  ElProgress,
  ElRadio,
  ElRadioGroup,
  ElRow: Row,
  ElSelect,
  ElSlider,
  ElSwitch,
  ElTable,
  ElTableColumn,
  ElTag,
};

export default {
  install(app) {
    Object.entries(adapterComponents).forEach(([name, component]) => {
      app.component(name, component);
    });
  },
};

export { ElMessage, ElMessageBox };
